import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Avatar } from '@ui/avatar/Avatar'
import { Voltar } from '@ui/shell/Voltar'
import { useAuth, useReadyUser } from '@ui/auth/auth-context'
import { Glass } from '@ui/glass/Glass'
import { SECAO_DO_PROMPT } from './InstrucoesDeAgente'
import { MemberList } from './MemberList'
import { useMembers } from './use-members'

/** O `<h2>` que dá nome à região dos membros. Há exatamente uma tela de Ajustes
 * na árvore por vez, então o id pode ser literal. */
const MEMBERS_HEADING_ID = 'ajustes-membros'

/**
 * Ajustes: perfil, membros da casa, sair. **Tela empilhada**, não aba — a barra
 * de quatro abas continua sendo a única navegação de primeiro nível
 * (Docs/04-Design/Navegação.md), e daqui se volta para a aba de origem.
 *
 * Nada de editar membro, de convidar e de trocar de casa: Docs/02-Dominio/Household.md
 * diz que essas coisas não existem neste app. Sem convite (allowlist no
 * código), sem papéis (todo membro tem o mesmo poder), e uma casa só por
 * pessoa.
 */
export function Settings() {
  const user = useReadyUser()
  const { signOut } = useAuth()
  const members = useMembers()
  const [signOutFailed, setSignOutFailed] = useState(false)

  async function handleSignOut() {
    setSignOutFailed(false)
    try {
      await signOut()
      // E acabou aqui. Quem leva ao login é o `RequireAuth`, quando o
      // observador de sessão disser que não há mais sessão — navegar daqui
      // seria um segundo caminho para o mesmo estado, e dois caminhos divergem.
    } catch {
      setSignOutFailed(true)
    }
  }

  return (
    <section>
      {/* **O `Voltar` do sistema** (decisão 255), e não um botão só desta tela.

          Este aqui nasceu antes dele e resolvia sozinho o mesmo problema: sair
          pelo histórico, com um destino de queda para quando `/ajustes` é a
          PRIMEIRA entrada — URL digitada, link compartilhado, deep link de
          notificação, relançamento do PWA instalado. Aquilo foi medido aqui
          (`canGoBack() === false` e o clique não mudava a rota) e depois virou
          o comportamento padrão do componente compartilhado.

          Ficar com a cópia agora seria manter a única saída do app com uma
          aparência própria — e a chance de as duas divergirem no dia em que uma
          delas mudar. O destino de queda continua sendo o Dashboard, a aba dona
          do avatar por onde se entra. */}
      <div className="mb-2">
        <Voltar para="/">Início</Voltar>
      </div>
      <h1 className="titulo mb-4 text-2xl">Ajustes</h1>

      {/*
        O perfil vem da **sessão**, não do documento de membro.

        `displayName` e `photoURL` do documento congelam no primeiro login: o
        bootstrap roda em toda abertura do app, mas `linkMemberUid` devolve a
        lista intacta quando o uid já está vinculado, justamente para não
        reescrever `joinedAt`. Mostrar o valor congelado como "seu perfil"
        exibiria uma foto antiga como se fosse a atual. Para você, a sessão é a
        fonte fresca; para a outra pessoa, o documento é a única que existe —
        e é isso que a lista abaixo mostra.
      */}
      <section aria-label="Seu perfil" className="mb-4">
        <Glass level="frost" className="flex items-center gap-4 p-5">
          <Avatar displayName={user.displayName} photoURL={user.photoURL} size={64} />
          <div className="min-w-0">
            <p className="truncate text-lg font-semibold tracking-tight">
              {user.displayName ?? 'Sem nome'}
            </p>
            {user.email !== null && (
              <p className="truncate text-sm text-[var(--text-muted)]">{user.email}</p>
            )}
          </div>
        </Glass>
      </section>

      <section aria-labelledby={MEMBERS_HEADING_ID} className="mb-4">
        <Glass level="frost" className="p-5">
          <h2 id={MEMBERS_HEADING_ID} className="mb-3 text-sm font-semibold tracking-tight">
            Membros da casa
          </h2>

          {members.status === 'loading' && (
            <p role="status" className="text-sm text-[var(--text-muted)]">
              Carregando os membros…
            </p>
          )}
          {members.status === 'failed' && (
            <p role="alert" className="text-sm text-[var(--text-muted)]">
              Não foi possível carregar os membros agora.
            </p>
          )}
          {members.status === 'loaded' && (
            <MemberList members={members.members} currentUserId={user.uid} />
          )}
        </Glass>
      </section>

      {/* Categorias antes do contexto de agente: mexer em categoria é
          manutenção de rotina da casa, e o contexto é coisa de configurar uma
          vez. */}
      <Glass level="frost" className="mb-4 p-5">
        <Link to="/ajustes/categorias" className="block">
          <span className="block text-sm font-semibold tracking-tight">Categorias</span>
          <span className="mt-1 block text-sm text-[var(--text-muted)]">
            Criar, renomear e arquivar as categorias e as subcategorias — onde o dinheiro da casa é
            agrupado.
          </span>
        </Link>
      </Glass>

      {/* Logo abaixo de Categorias, e é o pedido do dono: as duas são a mesma
          manutenção vista de dois lados — onde o dinheiro é agrupado, e o que
          agrupa sozinho. */}
      <Glass level="frost" className="mb-4 p-5">
        <Link to="/ajustes/regras" className="block">
          <span className="block text-sm font-semibold tracking-tight">Regras de categoria</span>
          <span className="mt-1 block text-sm text-[var(--text-muted)]">
            O que o app aprendeu a categorizar sozinho — e o que ele nunca deve categorizar. Toda
            transação nova com uma descrição conhecida já nasce classificada.
          </span>
        </Link>
      </Glass>

      {/* O contexto que os agentes leem. Fica atrás de um link, e não aberto
          aqui: é um formulário longo, e Ajustes é a tela curta por onde se
          passa para sair. */}
      <Glass level="frost" className="mb-4 flex flex-col gap-3 p-5">
        <Link to="/ajustes/agente" className="block">
          <span className="block text-sm font-semibold tracking-tight">Contexto para agentes</span>
          <span className="mt-1 block text-sm text-[var(--text-muted)]">
            O que um agente precisa saber antes de caçar preço: sites, cashback, desconto no PIX,
            onde a casa fica.
          </span>
        </Link>

        {/*
          O atalho para a prévia, em destaque e com âncora própria. Editar o
          contexto é raro; **ler o que o agente lê** é o que se faz toda vez que
          se vai passar isso para alguém — e sem o link direto seria abrir a
          tela e rolar seis cartões até o fim.
        */}
        <Link
          to="/ajustes/agente"
          hash={SECAO_DO_PROMPT}
          className="flex items-center gap-2 self-start rounded-[var(--radius-md)]
                     bg-[var(--brand)] px-3 py-2 text-sm font-semibold text-white"
        >
          Ver o que o agente lê
          <span aria-hidden="true">→</span>
        </Link>
      </Glass>

      {/* Sem diálogo de confirmação: sair não destrói nada e entrar de novo é
          um toque. Confirmação existe para o que não dá para desfazer. */}
      <button
        type="button"
        onClick={() => void handleSignOut()}
        className="w-full rounded-[var(--radius-md)] border border-[var(--border)] px-4 py-3
                   text-sm font-semibold text-[var(--negative)]"
      >
        Sair
      </button>
      {signOutFailed && (
        <p role="alert" className="mt-3 text-sm text-[var(--text-muted)]">
          Não foi possível sair agora. Tente de novo.
        </p>
      )}
    </section>
  )
}
